import { PersonalProviders } from "./personal-providers";
import { useEffect, useState } from "react";
import { Bot, ShieldCheck, BookMarked, Network, Plug } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { AIKnowledgeDialog } from "./ai-knowledge";
import { ProjectModelSharingDialog } from "./project-model-sharing";
import { PermissionSelector, type PermissionMode } from "./permission-selector";
export function ChatSettings({
  open,
  onClose,
  projectId,
  canManage,
  initialMemory,
  initialTab = "models",
  permissionMode,
  onPermission,
  onTask,
  onView,
}: {
  open: boolean;
  onClose(): void;
  projectId: string;
  canManage: boolean;
  initialMemory?: string;
  initialTab?: string;
  permissionMode: PermissionMode;
  onPermission(value: PermissionMode): void;
  onTask(id: string): void;
  onView(id: string): void;
}) {
  const fr = useI18n().language === "fr";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);
  const categories = [
    { id: "models", icon: Bot, label: fr ? "Modèles" : "Models" },
    {
      id: "permissions",
      icon: ShieldCheck,
      label: fr ? "Permissions" : "Permissions",
    },
    { id: "memory", icon: BookMarked, label: fr ? "Mémoire" : "Memory" },
    { id: "sources", icon: Network, label: fr ? "Sources" : "Sources" },
    { id: "mcp", icon: Plug, label: "MCP" },
  ].filter(category => projectId || ["models","permissions","memory"].includes(category.id));
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="grid-cols-1 max-w-3xl w-[calc(100%-2rem)] overflow-hidden p-0 gap-0 rounded-2xl">
        <DialogHeader className="border-b border-white/10 px-6 py-5 text-left pr-12">
          <DialogTitle>
            {fr ? "Paramètres du Chat AI" : "Chat AI settings"}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row h-[min(36rem,72dvh)] min-h-0 min-w-0">
          <nav className="flex sm:block min-w-0 w-full sm:w-44 shrink-0 overflow-x-auto sm:overflow-auto border-b sm:border-b-0 sm:border-r border-white/10 bg-white/[0.02] p-2 gap-1 sm:space-y-1">
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setTab(c.id)}
                className={cn(
                  "flex shrink-0 sm:w-full whitespace-nowrap items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-left transition-colors",
                  tab === c.id
                    ? "bg-primary/15 text-primary"
                    : "text-foreground-secondary hover:bg-white/5",
                )}
              >
                <c.icon className="h-4 w-4 shrink-0" />
                {c.label}
              </button>
            ))}
          </nav>
          <div className="flex-1 min-h-0 min-w-0 overflow-y-auto p-4 sm:p-6">
            <h3 className="mb-4 font-semibold">
              {categories.find((c) => c.id === tab)?.label}
            </h3>
            {tab === "models" ? (
              !projectId ? <PersonalProviders /> : <ProjectModelSharingDialog
                embedded
                open={open}
                projectId={projectId}
                onClose={onClose}
              />
            ) : tab === "permissions" ? (
              <div className="space-y-4">
                <p className="text-sm text-foreground-muted">
                  {fr
                    ? "Choisissez ce que cet assistant peut faire pour vos prochaines demandes. Vos permissions dans le projet restent appliquées."
                    : "Choose what the assistant may do for your next requests. Your project permissions still apply."}
                </p>
                <PermissionSelector
                  value={permissionMode}
                  onChange={onPermission}
                />
              </div>
            ) : (
              <AIKnowledgeDialog
                key={tab}
                embedded
                activeTab={tab as "memory" | "sources" | "mcp"}
                open={open}
                onClose={onClose}
                projectId={projectId}
                canManage={canManage}
                initialMemory={initialMemory}
                onTask={onTask}
                onDocument={onView}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
